import { intentStatusSchema } from "@redotech/redo-conversion-settings-common/predictive-conversion-settings";
import * as z from "zod";

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD")
  .refine((value) => {
    // Reject shape-valid but impossible dates (e.g. 2026-02-31) that Temporal
    // would otherwise silently constrain; round-trip through UTC unchanged.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const [year, month, day] = value.split("-").map(Number) as [
      number,
      number,
      number,
    ];
    const date = new Date(Date.UTC(year, month - 1, day));
    return (
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === month - 1 &&
      date.getUTCDate() === day
    );
  }, "Not a real calendar date");

/** Whether the merchant's Shopify app is still installed (has an enabled integration). */
export const ppuInstallStatusSchema = z.enum(["installed", "churned"]);
export type PpuInstallStatus = z.infer<typeof ppuInstallStatusSchema>;

/**
 * Free-trial lifecycle, gated on a billing record explicitly flagged as a trial
 * (`isSelfServeBillingTrial(trialType)`); "none" until a trial flow stamps one.
 */
export const ppuTrialStateSchema = z.enum([
  "active",
  "paying",
  "lapsed",
  "none",
]);
export type PpuTrialState = z.infer<typeof ppuTrialStateSchema>;

export const ppuIntentSelfServeMerchantRowSchema = z.object({
  teamId: z.string(),
  name: z.string(),
  storeUrl: z.string(),
  installStatus: ppuInstallStatusSchema,
  trialState: ppuTrialStateSchema,
  /** Whole days until the trial ends. Null unless `trialState === "active"`. */
  trialDaysLeft: z.number().nullable(),
  intentStatus: intentStatusSchema,
  /** Stored merchant-size assumption; null when never configured. */
  annualOrders: z.number().nullable(),
  /** Post-purchase upsell metrics over the window; null when no data in range. */
  upsellVisits: z.number().nullable(),
  upsellAcceptedOffers: z.number().nullable(),
  upsellRevenue: z.number().nullable(),
  /** Rescue-panel metrics over the window; null when no data in range. */
  impressions: z.number().nullable(),
  engagements: z.number().nullable(),
  rescueOrders: z.number().nullable(),
  recoveredRevenue: z.number().nullable(),
  /**
   * Store currency from a successful rescue-metrics fetch. May be null while
   * `upsellRevenue` is non-null (upsell-only merchants, or Snowflake returned
   * no currency for a zero-activity window) — never assume USD on the client.
   */
  currency: z.string().nullable(),
});
export type PpuIntentSelfServeMerchantRow = z.infer<
  typeof ppuIntentSelfServeMerchantRowSchema
>;

export const getPpuIntentSelfServeMerchants = {
  name: "getPpuIntentSelfServeMerchants" as const,
  input: z
    .object({ startDate: isoDate, endDate: isoDate })
    .refine(({ startDate, endDate }) => startDate <= endDate, {
      message: "startDate must be on or before endDate",
      path: ["startDate"],
    }),
  output: z.object({
    rows: z.array(ppuIntentSelfServeMerchantRowSchema),
    /** Total PPU-intent merchants (row count). */
    merchantsTotal: z.number(),
    /** Merchants whose trial is still active. 0 until a trial flow exists. */
    activeTrials: z.number(),
    totalRescueOrders: z.number(),
    /**
     * Cross-currency raw sum — each merchant's `upsellRevenue` is in its own
     * currency, so this is not FX-normalized (not USD). Rendered as a plain number.
     */
    totalUpsellRevenue: z.number(),
    /** Total accepted post-purchase upsell offers across merchants. */
    totalAcceptedOffers: z.number(),
    /** Merchants with any rescue-panel or upsell data in range. */
    metricsReporting: z.number(),
  }),
};
